import React, { useState } from 'react';
import { useTranslation } from '../i18n';
import LangToggle from './LangToggle';

// Standalone page shown at /reset-password?token=… (reached from the reset email).
// Fully backend-mediated: posts the token + new password to /auth/reset-password.
export default function ResetPassword() {
  const { t } = useTranslation();
  const token = (() => {
    try { return new URLSearchParams(window.location.search).get('token') || ''; }
    catch (e) { return ''; }
  })();

  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const goSignIn = () => { window.location.href = '/'; };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8) { setError(t('reset.too_short')); return; }
    if (pw !== confirm) { setError(t('reset.mismatch')); return; }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: pw })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || t('reset.error')); setLoading(false); return; }
      setDone(true);
    } catch {
      setError(t('login.error_connect'));
    }
    setLoading(false);
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.brandRow}>
          <span style={styles.wm}><span style={{ color: '#14653a' }}>Mow</span><span style={{ color: '#1f9d55' }}>Stop</span></span>
          <LangToggle />
        </div>

        {!token ? (
          <>
            <h1 style={styles.title}>{t('reset.invalid_title')}</h1>
            <p style={styles.sub}>{t('reset.invalid_link')}</p>
            <button type="button" style={styles.btn} onClick={goSignIn}>{t('reset.go_to_signin')}</button>
          </>
        ) : done ? (
          <>
            <h1 style={styles.title}>{t('reset.success_title')}</h1>
            <p style={styles.sub}>{t('reset.success_body')}</p>
            <button type="button" style={styles.btn} onClick={goSignIn}>{t('reset.go_to_signin')}</button>
          </>
        ) : (
          <>
            <h1 style={styles.title}>{t('reset.title')}</h1>
            <p style={styles.sub}>{t('reset.sub')}</p>
            {error && <div role="alert" style={styles.error}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <div style={styles.field}>
                <label htmlFor="reset-pw" style={styles.label}>{t('reset.new_password')}</label>
                <input id="reset-pw" style={styles.input} type="password" value={pw}
                  onChange={(e) => setPw(e.target.value)} required minLength={8} autoComplete="new-password" />
              </div>
              <div style={styles.field}>
                <label htmlFor="reset-confirm" style={styles.label}>{t('reset.confirm_password')}</label>
                <input id="reset-confirm" style={styles.input} type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} required minLength={8} autoComplete="new-password" />
              </div>
              <button type="submit" style={styles.btn} disabled={loading}>
                {loading ? t('reset.saving') : t('reset.submit')}
              </button>
            </form>
            <button type="button" style={styles.linkBtn} onClick={goSignIn}>{t('login.back_to_signin')}</button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f3f4ef', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  card: { background: '#fff', borderRadius: 20, padding: 36, width: '100%', maxWidth: 420, border: '1px solid #e3e9df', boxShadow: '0 30px 80px rgba(20,101,58,0.10)' },
  brandRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 },
  wm: { fontSize: 22, fontWeight: 800, letterSpacing: '-0.5px', fontFamily: 'Manrope, -apple-system, sans-serif' },
  title: { fontSize: 23, fontWeight: 800, color: '#1a1a1a', margin: '0 0 6px', letterSpacing: '-0.4px', fontFamily: 'Manrope, -apple-system, sans-serif' },
  sub: { fontSize: 14, color: '#5f6b58', margin: '0 0 22px', lineHeight: 1.5 },
  error: { background: '#FEF2F2', color: '#dc2626', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 16 },
  field: { display: 'flex', flexDirection: 'column', marginBottom: 14 },
  label: { fontSize: 12, fontWeight: 600, color: '#5f6b58', marginBottom: 6 },
  input: { width: '100%', padding: '11px 13px', border: '1px solid #e3e9df', borderRadius: 9, fontSize: 14, color: '#1a1a1a', background: '#f7f9f4', boxSizing: 'border-box', fontFamily: 'inherit' },
  btn: { width: '100%', padding: 12, border: 'none', background: '#14653a', borderRadius: 10, fontSize: 15, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 },
  linkBtn: { display: 'block', margin: '16px auto 0', background: 'none', border: 'none', color: '#14653a', fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }
};
